import { ColumnDef, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { TableShimmeringDiv } from 'src/tables/Table';
import { PartialRecord } from 'src/utils/types/types';
import {
  ListingStatus,
  MarketplacePaymentState,
  PointOfSalePaymentState,
  PointOfSaleSaleStatus,
  PurchasePaymentStatus,
} from 'src/WebApiController';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { multiSelectFilter } from '../filters/filterFunctions';

const COLUMN_MIN_SIZE = 50;
const COLUMN_MAX_SIZE = 500;

export type StatusType =
  | 'Positive'
  | 'PositiveProgress'
  | 'Neutral'
  | 'Negative';

// Status mappings for different entity types
export const SALE_STATUS_MAPPING: Record<PointOfSaleSaleStatus, StatusType> = {
  [PointOfSaleSaleStatus.Fulfilled]: 'Positive',
  [PointOfSaleSaleStatus.PendingFulfillment]: 'PositiveProgress',
  [PointOfSaleSaleStatus.ProcessingFulfillment]: 'PositiveProgress',
  [PointOfSaleSaleStatus.PendingConfirmation]: 'PositiveProgress',
  [PointOfSaleSaleStatus.PendingAllocation]: 'PositiveProgress',
  [PointOfSaleSaleStatus.ProvisionalAllocation]: 'PositiveProgress',
  [PointOfSaleSaleStatus.PendingReshipment]: 'PositiveProgress',
  [PointOfSaleSaleStatus.Hold]: 'Neutral',
  [PointOfSaleSaleStatus.CancelledHold]: 'Neutral',
  [PointOfSaleSaleStatus.FulfillmentFailed]: 'Negative',
  [PointOfSaleSaleStatus.PendingRejection]: 'Negative',
  [PointOfSaleSaleStatus.Rejected]: 'Negative',
  [PointOfSaleSaleStatus.Wasted]: 'Negative',
};

export const SALE_PAYMENT_STATUS_MAPPING: Record<
  PointOfSalePaymentState,
  StatusType
> = {
  [PointOfSalePaymentState.Paid]: 'Positive',
  [PointOfSalePaymentState.Queued]: 'PositiveProgress',
  [PointOfSalePaymentState.Unpaid]: 'Neutral',
  [PointOfSalePaymentState.PartiallyPaid]: 'PositiveProgress',
  [PointOfSalePaymentState.PendingReversal]: 'Neutral',
  [PointOfSalePaymentState.Reversed]: 'Negative',
};

export const MARKETPLACE_SALE_PAYMENT_STATUS_MAPPING: Record<
  MarketplacePaymentState,
  StatusType
> = {
  [MarketplacePaymentState.Paid]: 'Positive',
  [MarketplacePaymentState.Queued]: 'PositiveProgress',
  [MarketplacePaymentState.Unpaid]: 'Neutral',
  [MarketplacePaymentState.PartiallyPaid]: 'PositiveProgress',
  [MarketplacePaymentState.Ineligible]: 'Neutral',
  [MarketplacePaymentState.PendingReversal]: 'Neutral',
  [MarketplacePaymentState.Reversed]: 'Negative',
};

export const ListingStatusMapping: Record<ListingStatus, StatusType> = {
  [ListingStatus.Listed]: 'Positive',
  [ListingStatus.ListingPending]: 'PositiveProgress',
  [ListingStatus.FullySold]: 'Positive',
  [ListingStatus.Disabled]: 'Neutral',
  [ListingStatus.Delisted]: 'Neutral',
  [ListingStatus.DelistingPending]: 'Neutral',
  [ListingStatus.ListingFailed]: 'Negative',
  [ListingStatus.DelistingFailed]: 'Negative',
};

export const PURCHASE_PAYMENT_STATUS_MAPPING: Record<
  PurchasePaymentStatus,
  StatusType
> = {
  [PurchasePaymentStatus.Paid]: 'Positive',
  [PurchasePaymentStatus.PartiallyPaid]: 'PositiveProgress',
  [PurchasePaymentStatus.Unpaid]: 'Neutral',
  [PurchasePaymentStatus.Failed]: 'Negative',
  [PurchasePaymentStatus.RefundNeeded]: 'Negative',
  [PurchasePaymentStatus.Refunded]: 'Negative',
  [PurchasePaymentStatus.PartiallyRefunded]: 'Negative',
};

const STATUS_COLOR_MAPPING: Record<
  StatusType,
  { backgroundColor: string; color: string }
> = {
  Positive: {
    backgroundColor: vars.color.surfaceSuccess,
    color: vars.color.textSuccess,
  },
  PositiveProgress: {
    backgroundColor: vars.color.surfaceWarning,
    color: vars.color.textWarningV2,
  },
  Neutral: {
    backgroundColor: vars.color.surfaceSecondary,
    color: vars.color.textPrimaryV2,
  },
  Negative: {
    backgroundColor: vars.color.surfaceError,
    color: vars.color.textErrorV2,
  },
};

interface StatusColumnOptions<TData, TStatus> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get the status value from data */
  accessor: (data: TData) => TStatus | null | undefined;
  /** Status mapping to convert status enum to StatusType. Can be a single mapping or a function that returns the mapping based on the data */
  statusMapping:
    | PartialRecord<string, StatusType>
    | ((data: TData) => PartialRecord<string, StatusType> | undefined);
  /** Content mapping to convert status enum to ContentId/string for display. Can be a single mapping or a function */
  contentMapping?:
    | PartialRecord<string, string>
    | ((data: TData) => PartialRecord<string, string> | undefined);
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Custom cell renderer - receives status type and value. This is a render function, not a component, so it CANNOT use React hooks. */
  customCellRenderer?: (props: {
    status: TStatus;
    statusType: StatusType | undefined;
    statusColors: { backgroundColor: string; color: string } | undefined;
    contentMap: PartialRecord<string, string> | undefined;
    original: TData;
    columnId: string;
    table: Table<TData>;
    defaultRenderer: () => React.ReactNode;
    userDefinedPrecision?: any; // Pass hook results here
  }) => React.ReactNode;
  /** Hook to call for user settings - will be called in the Cell component and passed to customCellRenderer */
  useUserSetting?: (columnId: string, sectionType: any) => any;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
  /** Custom label formatter - converts status enum to display string */
  labelFormatter?: (status: TStatus, original: TData) => string;
}

/**
 * Factory function to create status column definitions for tables.
 * Handles status display with color coding based on status type.
 *
 * @example
 * createStatusColumn({
 *   id: SalesTableColumnId.Status,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Status],
 *   accessor: (data) => data?.sale?.status,
 *   statusMapping: SaleStatusMapping,
 *   size: COLUMN_DEFAULT_SIZE_BASE,
 * })
 */
export const createStatusColumn = <
  TData,
  TStatus extends string | number | symbol,
>(
  options: StatusColumnOptions<TData, TStatus>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    statusMapping,
    contentMapping,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    align = 'left',
    enableColumnFilter = true,
    filterFn,
    sortingFn,
    customCellRenderer,
    shouldRender,
  } = options;

  // Smart default: use multiSelectFilter if filtering is enabled and no custom filterFn
  const actualFilterFn =
    filterFn ?? (enableColumnFilter ? multiSelectFilter : undefined);

  return {
    id,
    enableColumnFilter,
    ...(actualFilterFn && { filterFn: actualFilterFn }),
    ...(sortingFn && { sortingFn }),
    size,
    minSize,
    maxSize,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    meta: {
      ...(enableColumnFilter && { columnFilterVariant: 'multiSelect' }),
      align,
    },
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      // Call any hooks at the top level of this component
      const userSetting = options.useUserSetting?.(
        columnId,
        table.options.meta?.sectionType
      );

      const status = getValue<TStatus | null | undefined>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (shouldRender && !shouldRender(original, table)) {
        return <BaseCell />;
      }

      if (status == null) {
        return <BaseCell />;
      }

      const statusMap =
        typeof statusMapping === 'function'
          ? statusMapping(original)
          : statusMapping;

      const statusType = statusMap ? statusMap[status as string] : undefined;

      const contentMap =
        typeof contentMapping === 'function'
          ? contentMapping(original)
          : contentMapping;

      if (!contentMap) {
        return <BaseCell />;
      }

      const contentId = contentMap[String(status)];

      const defaultRenderer = () => (
        <BaseCell isFullWidth align={align}>
          <div
            style={{
              borderRadius: vars.borderRadius.m,
              padding: `${vars.spacing['xxs']} ${vars.spacing['sm']}`,
              width: '100%',
              ...(statusType && STATUS_COLOR_MAPPING[statusType]),
            }}
          >
            {contentId ? <Content id={contentId} /> : String(status)}
          </div>
        </BaseCell>
      );

      if (customCellRenderer) {
        return customCellRenderer({
          status: status as TStatus,
          statusType,
          statusColors: statusType
            ? STATUS_COLOR_MAPPING[statusType]
            : undefined,
          contentMap,
          original,
          columnId,
          table,
          defaultRenderer,
          userDefinedPrecision: userSetting?.userDefinedPrecision,
        });
      }

      return defaultRenderer();
    },
  };
};
